// `ShortlistSingleProfileExportService` (cee70a51e8, then eace2a0e4b, 31e5213e47, f22e15d35d,
// f353e32a56) — SINGLE_PROFILE_EXPORT on the standalone candidate / client-contact profile, where
// there is no hunter list to own the export. It starts the async export, shows the lists' progress
// dialogue and polls the status — kept here, without a DOM or a clock, so every one of the four
// follow-up fixes can be pinned by a test.
//
// ⚠️ IT IS NOT THE LISTS' EXPORT WITH ONE ID, and `exportToAts.ts` is not reused for its rules:
//   · NOTHING IS RECORDED IN WOO. The lists follow a success with `exportCandidates(alertId, ids)`;
//     a profile has no alert, so the profile only flags its own copy of the entity as exported.
//   · "NOTIFY ME" DOES NOT MEAN EXPORTED. The lists' Notify me completes at once; here it resolves
//     'notify' unless a success has already been seen.
//   · A REJECTED START CLOSES THE DIALOGUE and resolves 'failed' (eace2a0e4b): no job exists, so
//     there is nothing to wait for, and the profile stays on the list picker so the user can retry.
//     The lists leave theirs spinning.
//   · THE SLOW FALLBACK RUNS FIRST ON EVERY TICK (f22e15d35d), independent of a pending status
//     call: a hanging status request must not keep the spinner up with no way out.
//   · ONE STATUS CALL AT A TIME (31e5213e47): a tick is skipped while one is in flight, so a slow,
//     older "not done" reply cannot overwrite a newer success.
//   · A FAILED STATUS CALL IS "NOT DONE YET" (eace2a0e4b): polling goes on.
import type { ExportListTarget, ExportStartedResponse, ExportStatusResponse } from '@woo-ext/api'
import { EXPORT_POLL_INTERVAL_MS, EXPORT_SLOW_AFTER_MS, plainExportTarget, type ExportToAtsView } from './exportToAts.ts'

/** `SingleProfileExportOutcome`. */
export type SingleProfileExportOutcome = 'exported' | 'notify' | 'failed'

/**
 * `newListRequest(kind, entity)` — for an ATS WITHOUT "add to existing list": export straight to a
 * new private list named after the profile. The lists use the hunter's name; this is the same
 * `{ name, description: '', isPublic: false }` shape.
 */
export function singleProfileNewListTarget(entityName: string | null | undefined): ExportListTarget {
  return plainExportTarget(entityName ?? '')
}

/**
 * The new-list name the picker opens with when there is no hunter: `updateCheckedCandidates` sets
 * `alertName = checked[0].name` when exactly one record is checked and nothing set it before.
 */
export function profileDefaultListName(checked: ReadonlyArray<{ name?: string | null }>): string {
  return checked.length === 1 ? (checked[0]?.name ?? '') : ''
}

/** What the dialogue renders. */
export interface SingleProfileExportView {
  /** The modal is showing. False after any of its buttons, the ✕, or a rejected start. */
  open: boolean
  /** `isExportCandidatesInProgress` / `isExportClientContactsInProgress` — one flag, two names. */
  inProgress: boolean
  /** `currentExportResponse`. */
  response: ExportStatusResponse | null
}

export function singleProfileExportDialogueView(view: SingleProfileExportView): ExportToAtsView {
  if (view.inProgress) return 'inProgress'
  return view.response?.success === true ? 'done' : 'slow'
}

export interface SingleProfileExportTimers {
  now: () => number
  setInterval: (callback: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
}

const REAL_TIMERS: SingleProfileExportTimers = {
  now: () => Date.now(),
  setInterval: (callback, ms) => globalThis.setInterval(callback, ms),
  clearInterval: (handle) => {
    globalThis.clearInterval(handle as ReturnType<typeof globalThis.setInterval>)
  },
}

export interface SingleProfileExportDeps {
  /** `exportToAtsListAsync` / `exportClientContactsToAtsListAsync`, already bound to its request. */
  start: () => Promise<ExportStartedResponse>
  /** `exportToAtsListAsyncStatus` — the one status endpoint both products poll. */
  status: (exportMessageId: number) => Promise<ExportStatusResponse>
  onChange: (view: SingleProfileExportView) => void
  /** Called once. Never for the ✕ — see `dismiss`. */
  onSettle: (outcome: SingleProfileExportOutcome) => void
  timers?: SingleProfileExportTimers
}

/**
 * One export, from the click to its outcome. `begin()` once; the dialogue's buttons call
 * `notifyMe()` / `goToAts()`; its ✕ calls `dismiss()`; the owner calls `dispose()` on unmount.
 */
export class SingleProfileExport {
  private view: SingleProfileExportView = { open: true, inProgress: true, response: null }
  private poll: unknown = undefined
  private settled = false
  private disposed = false
  private dismissed = false
  private begun = false
  private readonly timers: SingleProfileExportTimers

  constructor(private readonly deps: SingleProfileExportDeps) {
    this.timers = deps.timers ?? REAL_TIMERS
  }

  get current(): SingleProfileExportView {
    return this.view
  }

  /** `isDone()` — a status reply has reported success. */
  get done(): boolean {
    return this.view.response?.success === true
  }

  begin(): void {
    if (this.begun) return
    this.begun = true
    this.deps.start().then(
      (started) => {
        if (this.settled || this.disposed || this.dismissed) return
        this.startPolling(started.exportMessageId)
      },
      () => {
        // Rejected before a job was created: nothing to wait for, so drop the dialogue. The error
        // itself is the request layer's to report, as it is for the lists' export.
        this.update({ open: false })
        this.settle('failed')
      },
    )
  }

  /** `notifyMe()` — `ok()`, then 'exported' if a success was already seen, else 'notify'. */
  notifyMe(): void {
    this.update({ open: false })
    this.settle(this.done ? 'exported' : 'notify')
  }

  /**
   * `openInBullhorn()` — the "Done!" button. `ok()`, and 'exported'. Opening the hotlist on
   * Bullhorn is the caller's (`opensHotlistInAts`), with `current.response.outputParamsJson`.
   */
  goToAts(): void {
    this.update({ open: false })
    this.settle('exported')
  }

  /**
   * The header's ✕ (`dismiss('close')` — backdrop and Escape are prevented).
   *
   * ⚠️ UPSTREAM NEVER SETTLES HERE. Its `export()` promise stays pending, so the profile neither
   * flags the record nor leaves the picker, and its interval keeps polling until a success stops
   * it — after which nothing happens, because nothing is waiting. This port stops the poll at the
   * ✕ instead: the only thing the remaining polls could do is read, and no outcome is ever drawn
   * from them. What the user sees — dialogue gone, still on the picker, record not flagged — is
   * the same.
   */
  dismiss(): void {
    this.dismissed = true
    this.update({ open: false })
    this.stopPolling()
  }

  dispose(): void {
    this.disposed = true
    this.stopPolling()
  }

  private startPolling(exportMessageId: number): void {
    const startedAt = this.timers.now()
    let inFlight = false
    this.poll = this.timers.setInterval(() => {
      // f22e15d35d: the elapsed-time fallback FIRST, whatever any pending status call is doing.
      if (this.timers.now() - startedAt >= EXPORT_SLOW_AFTER_MS) this.stopSpinner()
      // 31e5213e47: one status call at a time, and none once done.
      if (inFlight || this.done) return
      inFlight = true
      this.deps
        .status(exportMessageId)
        .then(
          (response) => {
            if (this.disposed) return
            this.update({ response })
          },
          // eace2a0e4b: a failed status call is "not done yet" — keep polling.
          () => undefined,
        )
        .finally(() => {
          inFlight = false
          if (this.disposed || !this.done) return
          // The dialogue now shows "Done!" and settles on the user's click; stop polling.
          this.stopSpinner()
          this.stopPolling()
        })
    }, EXPORT_POLL_INTERVAL_MS)
  }

  private stopSpinner(): void {
    if (this.view.inProgress) this.update({ inProgress: false })
  }

  private stopPolling(): void {
    if (this.poll === undefined) return
    this.timers.clearInterval(this.poll)
    this.poll = undefined
  }

  /** `settle(outcome)` — cancels the poll, and resolves at most once. */
  private settle(outcome: SingleProfileExportOutcome): void {
    this.stopPolling()
    if (this.settled) return
    this.settled = true
    this.deps.onSettle(outcome)
  }

  private update(patch: Partial<SingleProfileExportView>): void {
    this.view = { ...this.view, ...patch }
    if (!this.disposed) this.deps.onChange(this.view)
  }
}
